/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { decodeTokenDetails, decodeTokenType, setTokenInCookie } from '../../_helpers/cookie'
import { History } from '../../_helpers/history'
import { handleMessages } from '../../_helpers/utilities'
import { IFilters } from '../../_models/commonInterface'
import { IDashboardState } from '../../_models/dashboardInterface'
import {
  IAddBusinessFilters,
  IBusinessIdParam,
  IBusinessState,
  IBusinessStatusDetails,
  IBusinessStatusParams,
  IEditBusiness,
  IProfileData,
} from '../../_models/superAdmin/businessInterface'
import * as businessService from '../../_services/superAdmin'
import { RootState } from '../../_store/store'
import { getBookingList, getDashboardStatistics } from '../dashboardSlice'
import { startLoader, stopLoader } from '../loaderSlice'
import { errorToast, successToast } from '../toastSlice'
import { userDetails } from '../userSlice'

const initialState: IBusinessState = {
  businessList: {
    current_page: 1,
    data: [],
    total: 20,
    per_page: 6,
    last_page: 0,
  },
  filters: {
    page: 1,
    per_page: 6,
    search: '',
  },
  isLoadingBusinessList: false,
  isLoadingProfileBusinessList: false,
  completeSuperAdminBusinessList: [],
  businessDrawer: false,
  businessInformation: null,
  addBusinessFilters: {
    name: '',
    url: '',
    email: '',
    address: '',
    description: '',
    latitude: '',
    longitude: '',
    location: '',
    cover: null,
  },
  profileBusinessList: {
    current_page: 1,
    data: [],
    total: 0,
    per_page: 10,
    last_page: 0,
  },
  profileFilters: {
    page: 1,
    per_page: 10,
    search: '',
  },
  completeProfileBusinessList: [],
  businessDetails: {
    id: 0,
    email: '',
    name: '',
    description: '',
    address: '',
    url: '',
    latitude: '',
    longitude: '',
    location: '',
    cover: '',
    created_at: '',
    status: 0,
    admin: '',
    unit_count: 0,
  },
  bookingsCount: 0,
  businessId: 0,
  businessDrawerMode: '',
  businessDetailsId: null,
  allocatedBusinessList: [],
  switchBusinessDetails: [],
  closeProfileDrawer: false,
  isDeleteModalOpen: false,
}

export const addBusiness = createAsyncThunk(
  '/addBusiness',
  async (addBusinessFilters: IAddBusinessFilters, { dispatch, rejectWithValue, getState }) => {
    const { superAdminBusiness } = getState() as { superAdminBusiness: IBusinessState }
    dispatch(startLoader())
    try {
      const response = await businessService.addBusiness(addBusinessFilters)
      dispatch(stopLoader())
      dispatch(successToast(handleMessages(response.message)))
      dispatch(updateBusinessDrawerStatus(false))
      dispatch(getSuperAdminBusinessList(superAdminBusiness.filters))
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const getBusinessDetails = createAsyncThunk(
  '/getBusinessDetails',
  async (businessId: IBusinessIdParam, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await businessService.getBusinessDetails(businessId)
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      return rejectWithValue(err)
    }
  },
)
export const editBusiness = createAsyncThunk(
  '/editBusiness',
  async (editParams: IEditBusiness, { dispatch, rejectWithValue, getState }) => {
    const { superAdminBusiness } = getState() as { superAdminBusiness: IBusinessState }
    dispatch(startLoader())
    try {
      const response = await businessService.editBusiness(editParams)
      dispatch(updateBusinessDrawerStatus(false))
      dispatch(successToast(handleMessages(response.message)))
      if (superAdminBusiness.businessDetailsId) {
        dispatch(getBusinessDetails({ businessId: superAdminBusiness.businessDetailsId }))
      }
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const getSuperAdminBusinessList = createAsyncThunk(
  '/getSuperAdminBusinessList',
  async (filters: IFilters, { dispatch, rejectWithValue }) => {
    try {
      const response = await businessService.getSuperAdminBusinessList(filters)
      return response
    } catch (err: any) {
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const getBusinessBookingCount = createAsyncThunk(
  '/getBusinessBookingCount',
  async (businessID: IBusinessIdParam, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await businessService.getBusinessBookingCount(businessID)
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const updateBusinessStatus = createAsyncThunk(
  '/updateStatus',
  async (businessStatus: IBusinessStatusDetails, { dispatch, rejectWithValue, getState }) => {
    const { superAdminBusiness } = getState() as { superAdminBusiness: IBusinessState }
    dispatch(startLoader())
    try {
      const { businessId, status, fetchData } = businessStatus
      const businessStatusParams: IBusinessStatusParams = { businessId: businessId, status: status }
      const response = await businessService.updateBusinessStatus(businessStatusParams)
      dispatch(successToast(handleMessages(response.message)))
      if (fetchData === 'businessDetails')
        dispatch(getBusinessDetails({ businessId: businessStatus.businessId }))
      else if (fetchData === 'businessList')
        dispatch(getSuperAdminBusinessList(superAdminBusiness.filters))
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const switchSuperAdminBusinessList = createAsyncThunk(
  '/switchBusinessList',
  async (filters: IFilters | undefined, { dispatch, rejectWithValue }) => {
    try {
      const response = await businessService.switchSuperAdminBusinessList(filters)
      return response
    } catch (err: any) {
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const superAdminSwitchProfile = createAsyncThunk(
  '/switchProfile',
  async (profileData: IProfileData, { dispatch, rejectWithValue, getState }) => {
    dispatch(startLoader())
    try {
      const response = await businessService.switchProfile(profileData)
      setTokenInCookie(response.data.token)
      const userDetailsResponse = await dispatch(userDetails())
      const tokenDetails = decodeTokenDetails()
      if (userDetailsResponse) {
        if (
          decodeTokenType(response.data.token) === 'super_admin' &&
          tokenDetails?.active_business_id !== null
        ) {
          History.navigate('/admin/dashboard')
          dispatch(closeProfileDrawer())

          const { dashboard } = getState() as { dashboard: IDashboardState }
          dispatch(getBookingList(dashboard.dashboardBookingListData.filters))
          dispatch(getDashboardStatistics())
        } else {
          History.navigate('/super-admin/dashboard')
          dispatch(closeProfileDrawer())
        }
      }

      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const deleteBusiness = createAsyncThunk(
  '/deleteUser',
  async (id: number, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())

    try {
      const response = await businessService.deleteBusiness(id)
      dispatch(stopLoader())
      dispatch(closeBusinessDeleteModal())
      dispatch(successToast(handleMessages(response.message)))

      History.navigate('/super-admin/business-management')
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
const superAdminBusinessSlice = createSlice({
  name: 'superAdminBusiness',
  initialState,
  reducers: {
    changeFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetBusinessListData: (state) => {
      state.completeSuperAdminBusinessList = []
    },
    changeProfileFilters: (state, action) => {
      state.profileFilters = { ...state.profileFilters, ...action.payload }
    },
    resetProfileBusinessListData: (state) => {
      state.completeProfileBusinessList = []
    },
    updateBusinessDrawerStatus: (state, action) => {
      state.businessDrawer = action.payload
    },
    resetAddBusinessFormData: (state) => {
      state.addBusinessFilters = initialState.addBusinessFilters
    },
    updateBusinessDrawerMode: (state, action) => {
      state.businessDrawerMode = action.payload
    },
    updateBusinessDetailsId: (state, action) => {
      state.businessDetailsId = action.payload
    },
    closeProfileDrawer: (state) => {
      state.closeProfileDrawer = true
    },
    resetProfileDrawerClose: (state) => {
      state.closeProfileDrawer = false
    },
    openBusinessDeleteModal: (state) => {
      state.isDeleteModalOpen = true
    },
    closeBusinessDeleteModal: (state) => {
      state.isDeleteModalOpen = false
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(getSuperAdminBusinessList.pending, (state) => {
        state.businessList = initialState.businessList
        state.isLoadingBusinessList = true
      })
      .addCase(getSuperAdminBusinessList.fulfilled, (state, action) => {
        state.businessList = action.payload?.data?.businesses

        state.isLoadingBusinessList = false
      })
      .addCase(getSuperAdminBusinessList.rejected, (state) => {
        state.isLoadingBusinessList = false
      })
      .addCase(addBusiness.fulfilled, (state, action) => {
        state.businessInformation = action.payload.data
      })
      .addCase(getBusinessDetails.fulfilled, (state, action) => {
        state.businessDetails = action.payload.data.business
      })
      .addCase(getBusinessBookingCount.fulfilled, (state, action) => {
        state.bookingsCount = action.payload.data.bookings_count
      })
      .addCase(switchSuperAdminBusinessList.pending, (state) => {
        state.isLoadingProfileBusinessList = true
      })
      .addCase(switchSuperAdminBusinessList.fulfilled, (state, action) => {
        state.profileBusinessList = action.payload?.data
        if (action.payload.data?.data) {
          const newData = action.payload.data.data
          const updatedList = state.completeProfileBusinessList.map((item) => {
            const newItem = newData.find((data: { id: number }) => data.id === item.id)
            return newItem ? { ...item, ...newItem } : item
          })
          const newItems = newData.filter(
            (data: { id: number }) =>
              !state.completeProfileBusinessList.some((item) => item.id === data.id),
          )
          state.completeProfileBusinessList = [...updatedList, ...newItems]
        }

        state.isLoadingProfileBusinessList = false
      })
      .addCase(switchSuperAdminBusinessList.rejected, (state) => {
        state.isLoadingProfileBusinessList = false
      })
      .addCase(superAdminSwitchProfile.fulfilled, (state, action) => {
        state.switchBusinessDetails = action.payload.data
      }),
})

export const {
  changeFilters,
  resetBusinessListData,
  changeProfileFilters,
  updateBusinessDrawerStatus,
  resetAddBusinessFormData,
  updateBusinessDrawerMode,
  updateBusinessDetailsId,
  closeProfileDrawer,
  resetProfileDrawerClose,
  resetProfileBusinessListData,
  openBusinessDeleteModal,
  closeBusinessDeleteModal,
} = superAdminBusinessSlice.actions
export const superAdminBusinessState = (state: RootState) => state.superAdminBusiness
const superAdminBusinessReducer = superAdminBusinessSlice.reducer
export default superAdminBusinessReducer
